import { CommitOptions, MutationTree, Store } from "vuex";
import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import { MeResponse } from "common/src/api/me";
import { renewalSubmitRequest, renewalSubmitResponse } from "@/api/renewalSubmit";

/**
 * Storeのtype定義
 */
export type State = {
  me?: MeResponse;
  renewal?: renewalSubmitRequest;
  conveni?: renewalSubmitResponse;
  serialNo: string;
  needDisclosure?: boolean;
  mail?: string;
  hasAnother?: string;
};

/**
 * Vuexによる管理を行う対象
 */
export const state: State = {
  me: undefined,
  renewal: undefined,
  conveni: undefined,
  serialNo: "",
  needDisclosure: undefined,
  mail: undefined,
  hasAnother: undefined,
};

/**
 * Mutation定義
 */
export enum MutationTypes {
  /**
   * 認証者情報をセット
   */
  SET_ME = "SET_ME",
  /**
   * パスワード変更実施
   */
  PASSWORD_CHANGED = "PASSWORD_CHANGED",
  /**
   * 重要事項説明が必要かどうか
   */
  SET_NEED_DISCLOSURE = "SET_NEED_DISCLOSURE",
  /**
   * 更新時のメールアドレス送信にて取得したシリアルセット処理
   */
  SET_RENEWAL_SERIAL = "SET_RENEWAL_SERIAL",
  /**
   * 更新契約手続きFormセット
   */
  SET_RENEWAL = "SET_RENEWAL",
  /**
   * コンビニセット
   */
  SET_CONVENI = "SET_CONVENI",
  /**
   * 更新データのリセット処理
   */
  RESET_RENEWAL = "RESET_RENEWAL",
  /**
   * 更新に取得するメールアドレスのセット処理
   */
  SET_MAIL = "SET_MAIL",
  /**
   * その他保険のセット処理
   */
  SET_ANOTHER = "SET_ANOTHER",
  /**
   * メールアドレス変更のシリアル番号
   */
  SET_SERIAL = "SET_SERIAL",
  /**
   * コンビニ情報セット処理
   */
  SET_CONVENI_RESPONSE = "SET_CONVENI_RESPONSE",
  /**
   * ストアのリセット処理
   */
  RESET = "RESET",
}

/**
 * Mutation実装
 */
export type Mutations<S = State> = {
  [MutationTypes.SET_ME](state: S, me: MeResponse): void;
  [MutationTypes.PASSWORD_CHANGED](state: S): void;
  [MutationTypes.SET_NEED_DISCLOSURE](state: S, isNeed: boolean): void;
  [MutationTypes.SET_RENEWAL_SERIAL](state: S, serial: string): void;
  [MutationTypes.SET_RENEWAL](state: S, form: renewalSubmitRequest): void;
  [MutationTypes.SET_CONVENI](state: S, conveni: string): void;
  [MutationTypes.SET_MAIL](state: S, mail: string): void;
  [MutationTypes.SET_ANOTHER](state: S, another: string): void;
  [MutationTypes.RESET_RENEWAL](state: S): void;
  [MutationTypes.SET_SERIAL](state: S, serial: string): void;
  [MutationTypes.SET_CONVENI_RESPONSE](state: S, conveni: renewalSubmitResponse): void;
  [MutationTypes.RESET](state: S): void;
};

/**
 * Mutation実装
 */
export const mutations: MutationTree<State> & Mutations = {
  [MutationTypes.SET_ME](state: State, me: MeResponse) {
    state.me = me;
  },
  [MutationTypes.PASSWORD_CHANGED](state: State) {
    if (state.me) {
      state.me.needPasswordChange = false;
    }
  },
  [MutationTypes.SET_NEED_DISCLOSURE](state: State, isNeed: boolean) {
    state.needDisclosure = isNeed;
  },
  [MutationTypes.SET_RENEWAL](state: State, form: renewalSubmitRequest) {
    state.renewal = form;
  },
  [MutationTypes.SET_RENEWAL_SERIAL](state: State, serial: string) {
    if (!state.renewal) {
      state.renewal = {
        rnw01p: {},
        rnw02p: {},
        security: { tfHnuuid: serial },
      };
    } else {
      state.renewal.security = { tfHnuuid: serial };
    }
  },
  [MutationTypes.SET_CONVENI](state: State, conveni: string) {
    if (state.renewal) {
      state.renewal.rnw01p.tfReconv = conveni;
    }
  },
  [MutationTypes.SET_MAIL](state: State, mail: string) {
    state.mail = mail;
  },
  [MutationTypes.SET_ANOTHER](state: State, another: string) {
    state.hasAnother = another;
  },
  [MutationTypes.RESET_RENEWAL](state: State) {
    state.renewal = undefined;
    state.conveni = undefined;
    state.needDisclosure = undefined;
    state.hasAnother = undefined;
    state.mail = undefined;
  },
  [MutationTypes.SET_SERIAL](state: State, serial: string) {
    state.serialNo = serial;
  },
  [MutationTypes.SET_CONVENI_RESPONSE](state: State, conveni: renewalSubmitResponse) {
    state.conveni = conveni;
  },
  [MutationTypes.RESET](state: State) {
    state.me = undefined;
    state.renewal = undefined;
    state.conveni = undefined;
    state.serialNo = "";
  },
};

/**
 * ストア定義
 */
export type AppStore<S = State> = Omit<Store<S>, "commit"> & {
  commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(key: K, payload?: P, options?: CommitOptions): ReturnType<Mutations[K]>;
};

/**
 * ストア作成
 */
export const globalStore = createStore({
  plugins: [
    createPersistedState({
      key: "Mypage",
      storage: window.sessionStorage,
    }),
  ],
  mutations: mutations,
  state: state,
});

/**
 * 各コンポーネントでストアを使用するためのメソッド
 * @returns SessionStorageに格納されたストア
 */
export function useStore(): AppStore {
  return globalStore as AppStore;
}
